/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {EEmbededElementTypes, RenderDocument} from "js_renderer";
import Element from "./element";
import Factory from "./factory";
import {getIndexToFlattenedTree3} from "../globe/common";

export default
class ChunkLODNode extends Element {

    public static TypeName: string = EEmbededElementTypes.CHUNKLODNODE;

    private _metadata: any;
    private _patch: any;
    private _uri: string;
    private _localData: any;
    private _trafo: (number)[];

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._metadata = null;
        this._patch = null;
    }

    public static create(aRenderDocument: RenderDocument): ChunkLODNode {
        let element = new ChunkLODNode(aRenderDocument);
        element._typeName = ChunkLODNode.TypeName;
        return element;
    }

    public setPatch(aPatch: any) {
        this._patch = aPatch;
    }

    public getVisibility(): boolean {
        return this._patch.visibility[getIndexToFlattenedTree3(this._metadata.level, this._metadata.layer, this._metadata.row, this._metadata.col, this._metadata.nc)];
    }

    public setData(aData: any) {
        this._metadata = aData.metadata;
        this._metadata.patchInfo = aData.patchInfo;

        this._properties.bboxMin = aData.metadata.bbox.min;
        this._properties.bboxMax = aData.metadata.bbox.max;
        this._properties.positionOffset = aData.positionOffset;

        this._properties.positions = aData.positions.buffer;
        this._properties.normals = aData.normals.buffer;
        this._properties.texCoords = aData.texCoords.buffer;
        this._properties.layers = aData.layers.buffer;
        this._properties.surfaceStyles = aData.surfaceStyles.buffer;
        this._properties.vertexCount = aData.metadata.nofPoints;
        this._properties.normalCount = aData.metadata.nofPoints;
        this._properties.texcoordCount = aData.metadata.nofPoints;
        this._properties.layersCount = aData.metadata.nofPoints;

        this._properties.indexCount = aData.metadata.nofFaces * 3;
        this._properties.indices = aData.indices;

        this._properties.faceSize = [this._properties.indexCount];
        this._properties.offset = 0;
        this._properties.binSize = aData.positions.byteLength + aData.normals.byteLength + aData.texCoords.byteLength + aData.layers.byteLength + aData.surfaceStyles.byteLength + aData.indices.byteLength;

        this.setTexture(aData);
    }

    public releaseData() {
        this._properties.positions = null;
        this._properties.normals = null;
        this._properties.texCoords = null;
        this._properties.layers = null;
        this._properties.surfaceStyles = null;
    }

    public setTexture(aData: any) {
        this._uri = this._renderDocument.getRenderer()._dataStorage.createLocalResourceUri();
        this._localData = aData.textureData;

        let rowToPhysicalSpaceTrafo = Math.pow(2, aData.metadata.level); // to transform the coordinates back to physical texture space, at level 0, the physical space equals the virtual space
        let rowToVirtualSpaceTrafo = 1 / rowToPhysicalSpaceTrafo;
        let rowOffset = aData.metadata.row * rowToVirtualSpaceTrafo;

        let colToPhysicalSpaceTrafo = Math.pow(4, aData.metadata.level); // to transform the coordinates back to physical texture space, at level 0, the physical space equals the virtual space
        let colToVirtualSpaceTrafo = 1 / colToPhysicalSpaceTrafo;
        let colOffset = (aData.metadata.col / Math.pow(2, aData.metadata.patchInfo.levels - 1 - aData.metadata.level)) * colToVirtualSpaceTrafo;

        this._trafo = [colOffset, rowOffset, colToPhysicalSpaceTrafo, rowToPhysicalSpaceTrafo];
    }

    protected _generateElementDescriptionJSON(): any {
        return {
            "type": this.getTypeName(),
            "id": this.getTypeName() + "_" + this._UID, // this is is not important in the engine, it can identify e.g. id of an element in Lexocad
            "UID": this._UID
        };
    }

    protected _generateGeometryObjectDescriptionJSON(): any {
        let appearanceContent: any = {};

        appearanceContent.renderOption = {
            glodXOffset: this._properties.positionOffset[0],
            glodYOffset: this._properties.positionOffset[1]
        };

        let urls = [];
        let widths = [];
        let heights = [];
        let offsets = [];
        let sizes = [];
        let tiled = [];

        let nofLods = this._metadata.nofTextureLODS;
        if (nofLods > 0 && this._metadata.level !== this._metadata.patchInfo.levels - 1) {
            nofLods = 1; // use texture lod only for the highest detail
        }

        for (let i = 0; i < nofLods; i++) {
            if (i === 0) {
                urls.push(this._uri);
            } else {
                let texUrl = this._metadata.baseAddress + this._metadata.level + "-" + this._metadata.layer + "-" + this._metadata.row + "-" + this._metadata.col + "-" + i + ".jpg";
                urls.push(texUrl);
            }

            widths.push(this._metadata.widths[i]);
            heights.push(this._metadata.heights[i]);
            offsets.push(0);
            sizes.push(0);
            tiled.push(false);
        }

        appearanceContent.imageTexture = {
            url: urls,
            width: widths,
            height: heights,
            offsets: offsets,
            sizes: sizes,
            tiled: tiled,
            alpha: false,
            model: "REPLACE",
            blendColor: [0.00392157, 0.00392157, 0.00392157],
            repeatS: 0,
            repeatT: 0,
            trafo: this._trafo.slice(),
            glodLevelSize: Math.pow(2, this._metadata.patchInfo.levels - 1 - this._metadata.level),
            localData: this._localData
        };

        this._localData = null;
        return [{
            description: {
                type: "CHUNKLODNODESHAPE",
                bboxMin: [this._properties.bboxMin[0], this._properties.bboxMin[1], this._properties.bboxMin[2]],
                bboxMax: [this._properties.bboxMax[0], this._properties.bboxMax[1], this._properties.bboxMax[2]]
            },
            appearance: appearanceContent,
            volumetricIndexedFaceSet: {
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceUri(),
                vertexCount: this._properties.vertexCount,
                normalCount: this._properties.normalCount,
                texcoordCount: this._properties.texcoordCount,
                layersCount: this._properties.layersCount,
                indexCount: this._properties.indexCount,
                binSize: this._properties.binSize,
                positions: new Float32Array(this._properties.positions),
                normals: new Float32Array(this._properties.normals),
                texCoords: new Float32Array(this._properties.texCoords),
                layers: new Float32Array(this._properties.layers),
                indices: this._properties.indices
            }
        }];
    }
}

Factory.register(ChunkLODNode);
